<script lang="ts">
import GLILogo from '@/assets/images/partners/GLI-logo.png'
import { useRouteMeta } from '@/plugins/useRouteMeta'
import { SettingsService } from '@/services/settings'
import { compareFn, groupBy, toValue } from '@/utils/utils'
import { ThemeType } from '@goodlifeplus/themes'
import { defineComponent } from 'vue'
import type { RouteRecordRaw } from 'vue-router'

import { Social } from './AppSocialIcons.vue'

/**
 * Displays a footer.
 */
export default defineComponent({
  setup() {
    return {
      Social,
      GLILogo,
      routeMeta: useRouteMeta().meta,
      ThemeType,
      minimumAge: SettingsService.isCaLocale() ? 19 : 18,
    }
  },
  computed: {
    navRoutes(): Record<
      string,
      Array<{
        path: string
        category: string
        text: string
        isExternal: boolean
        fullPath: boolean
      }>
    > {
      let routes = this.$router.options.routes.filter(
        (route: RouteRecordRaw) =>
          (route.meta?.nav?.footer?.display ?? false) || route.children
      )
      routes = routes.reduce(
        (routes: Array<RouteRecordRaw>, route: RouteRecordRaw) => {
          if (route.children) {
            routes = routes.concat(
              route.children.filter((route: RouteRecordRaw) =>
                toValue(route.meta?.nav?.footer?.display ?? false)
              )
            )
          }

          if (
            route.path &&
            toValue(route.meta?.nav?.footer?.display ?? false)
          ) {
            routes.push(route)
          }

          return routes
        },
        []
      )

      const footerRoutes = routes.map((route: RouteRecordRaw) => ({
        path: window.location.origin.includes(this.BASE_URL)
          ? route.path
          : `${this.BASE_URL}${route.path}`,
        category: route.meta?.nav?.footer?.category ?? 'other',
        text: toValue(route.meta?.nav?.footer?.text) as string,
        isExternal: false,
        fullPath: !window.location.origin.includes(this.BASE_URL),
      }))

      footerRoutes.sort((a, b) => compareFn(a.text, b.text))

      const groupedFooterNav = groupBy((r) => r.category, footerRoutes)

      if (!groupedFooterNav.company) {
        groupedFooterNav.company = []
      }

      return groupedFooterNav
    },
    isPartnershipCampaignView(): boolean {
      return !!this.routeMeta.nav?.header?.logoImage
    },
    isTrueNorthTheme(): boolean {
      return this.$theme.name === 'true-north'
    },
  },
  methods: {
    categoryToText(category: string): string {
      switch (category) {
        case 'company':
          return 'Company'

        case 'support':
          return 'Help & Support'

        case 'other':
          return 'Other'
      }

      return category
    },
  },
})
</script>

<template>
  <footer
    v-once
    class="bg-black px-8 py-8 pb-28 text-pure-white md:pb-12 footer"
  >
    <img
      v-if="isPartnershipCampaignView"
      :src="$theme.assets.logoLight"
      :title="`${BUSINESS_NAME} Logo`"
      :alt="`${BUSINESS_NAME} Logo`"
      height="24"
      width="180"
      class="mb-6 footer-logo"
    />

    <RouterLink
      v-if="!isPartnershipCampaignView"
      :to="{ name: 'home' }"
      class="!border-0"
    >
      <img
        :src="$theme.assets.logoLight"
        :title="`${BUSINESS_NAME} Logo`"
        class="mb-6 footer-logo"
      />
    </RouterLink>

    <div class="flex flex-col gap-8 @md/footer:flex-row">
      <div class="flex-grow italic">
        <template v-if="BUSINESS_PHONE">
          <div class="text-lg font-bold">Questions?</div>
          <div class="text-2xl font-black mb-8" tabindex="0">
            Call us on
            <a :href="`tel:${BUSINESS_PHONE}`" class="link inline-block">
              {{ BUSINESS_PHONE }}
            </a>
          </div>
        </template>

        <AppSocialIcons
          :socials="[
            Social.Facebook,
            Social.X,
            Social.Youtube,
            Social.Instagram,
          ]"
        >
          <template v-if="!isTrueNorthTheme" #footer>
            Join Our 550,000 Followers
          </template>
        </AppSocialIcons>
      </div>

      <nav class="grid grid-cols-1 gap-4 @sm/footer:grid-cols-3">
        <div v-for="(routeGroup, category) in navRoutes" :key="category">
          <div class="mb-3 text-lg font-bold">
            {{ categoryToText(category) }}
          </div>

          <ol role="list" class="flex flex-col gap-3">
            <li v-for="(route, key) in routeGroup" :key>
              <component
                :is="route.fullPath ? 'a' : 'router-link'"
                :href="route.path"
                :to="route.path"
                :target="route.isExternal ? '_blank' : '_self'"
                class="block rounded-md bg-slate-600 p-3 px-4 font-medium leading-none"
              >
                {{ route.text }}
              </component>
            </li>
          </ol>
        </div>
      </nav>
    </div>

    <div class="flex flex-col gap-3">
      <p class="mt-8 text-justify">
        You must be over {{ minimumAge }} to enter. If you are under
        {{ minimumAge }} your ticket will be void and refunded.

        <template v-if="$theme.siteConfig.gamblingCommissionUrl">
          <a
            :href="`//${$theme.siteConfig.gamblingCommissionUrl}`"
            class="link"
            target="_blank"
          >
            {{ $theme.siteConfig.gamblingCommissionUrl }}</a
          >. </template
        >Our prize draws are in no way sponsored or endorsed by Facebook or
        Instagram. Draws will be made using independent verification software.
      </p>

      <p>&copy; {{ DateTime.now().toFormat('yyyy') }} {{ BUSINESS_NAME }}</p>

      <a
        v-if="$theme.type !== ThemeType.Subscription"
        href="https://access.gaminglabs.com/Certificate/Index?i=635"
        class="link"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img :src="GLILogo" title="GLI Logo" class="h-[48px] w-auto mt-4" />
      </a>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
a.link {
  @apply text-pure-white;
}
</style>
